import { Box, Grid, Switch } from '@mui/material';
import { useEffect } from 'react';
import { FormProvider, Controller, useForm } from 'react-hook-form';
import { WsButton } from 'wisestep-base';

import { DrawerFooter } from '../../../components/filters/components/AdvanceFilterFooterBar';
import FormSwitchLayout from '../../careerPageSettings/components/formSwitchLayout';
import { getDefaultSelectedColumns } from '../helper/crmJobHelper';

interface CrmJobDownloadFieldProps {
  selectedFields: (fields: string[]) => void;
  close: () => void;
  availableColumns: string[];
}

const CrmJobDownloadField = ({
  selectedFields,
  close,
  availableColumns,
}: CrmJobDownloadFieldProps) => {
  const defaultSelected = getDefaultSelectedColumns();
  const defaultValues = availableColumns.reduce((acc, col) => {
    acc[col] = defaultSelected.includes(col);
    return acc;
  }, {} as Record<string, boolean>);

  const methods = useForm({ defaultValues });
  const { handleSubmit, setValue, control, reset, getValues } = methods;

  const handleSelectAll = () => availableColumns.forEach((col) => setValue(col, true));
  const handleClearAll = () => availableColumns.forEach((col) => setValue(col, false));
  const onDownload = (data: Record<string, boolean>) => {
    const selected = Object.entries(data)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (selected.length > 0) selectedFields(selected);
  };

  const getColumnDisplayName = (column: string): string => {
    const displayNames: { [key: string]: string } = {
      id: 'ID',
      title: 'Job Title',
      jobStatus: 'Job Status',
      createdOn: 'Created On',
      client: 'Client',
      employmentType: 'Employment Type',
      linkedInJobUrl: 'LinkedIn Job URL',
      jobDescription: 'Job Description',
      postedDate: 'Posted Date',
      location: 'Location',
      jobSource: 'Job Source',
      jobApplyUrl: 'Job Apply URL',
      totalApplicants: 'Total Applicants',
      jobClosed: 'Job Closed',
      createdBy: 'Created By',
      modifiedBy: 'Modified By',
      createdByName: 'Created By Name',
      modifiedByName: 'Modified By Name',
      modifiedOn: 'Modified On',
      clientContactCount: 'Client Contact Count',
      eligibleContactCount: 'Eligible Contact Count',
      taggedContactCount: 'Tagged Contact Count',
      mailing: 'Mailing List',
    };
    return displayNames[column] || column;
  };

  useEffect(() => {
    const newDefaults = availableColumns.reduce((acc, col) => {
      acc[col] = defaultSelected.includes(col);
      return acc;
    }, {} as Record<string, boolean>);
    reset(newDefaults);
  }, [availableColumns, reset]);

  const selectedCount = Object.values(getValues()).filter(Boolean).length;

  return (
    <FormProvider {...methods}>
      <Box>
        <Grid container p={1} justifyContent="flex-end" alignItems="center">
          <Grid item>
            <WsButton variant="text" size="medium" onClick={handleSelectAll}>
              Select All
            </WsButton>
            <WsButton variant="text" size="medium" onClick={handleClearAll}>
              Clear All
            </WsButton>
          </Grid>
        </Grid>
        <Grid container xs={12} gap={1} m={1}>
          {availableColumns.map((col) => (
            <FormSwitchLayout
              key={col}
              label={getColumnDisplayName(col)}
              gridValue={5.8}
              border="solid"
              justifyContent="space-between"
            >
              <Box pt={1}>
                <Controller
                  name={col}
                  control={control}
                  render={(props) => (
                    <Switch
                      checked={props.field.value}
                      onChange={() => setValue(col, !props.field.value)}
                    />
                  )}
                />
              </Box>
            </FormSwitchLayout>
          ))}
        </Grid>
        <DrawerFooter>
          <Grid container p={1} spacing={2} direction="row" justifyContent="flex-end">
            <Grid item>
              <WsButton variant="outlined" size="large" onClick={close}>
                Cancel
              </WsButton>
            </Grid>
            <Grid item>
              <WsButton
                variant="contained"
                size="large"
                onClick={handleSubmit(onDownload)}
                disabled={selectedCount === 0}
              >
                Download
              </WsButton>
            </Grid>
          </Grid>
        </DrawerFooter>
      </Box>
    </FormProvider>
  );
};

export default CrmJobDownloadField;
